import { ethers } from "ethers";
import { getEthersProvider } from "./wagmiProvider";

// Multicall3 contract address (same across all chains)
const MULTICALL3_ADDRESS = "0xcA11bde05977b3631167028862bE2a173976CA11";

// Popular BEP20 tokens on BSC
const BSC_TOKENS = {
  USDT: "0x55d398326f99059fF775485246999027B3197955",
  USDC: "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d", 
  WBNB: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c",
  BUSD: "0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56",
  DAI: "0x1AF3F329e8BE154074D8769D1FFa4eE058B1DBc3",
};

// ERC20 balanceOf function signature
const BALANCE_OF_SIGNATURE = "0x70a08231";

// Multicall3 ABI (minimal)
const MULTICALL3_ABI = [
  {
    "inputs": [
      {
        "components": [
          {"internalType": "address", "name": "target", "type": "address"},
          {"internalType": "bytes", "name": "callData", "type": "bytes"}
        ],
        "internalType": "struct Multicall3.Call[]",
        "name": "calls",
        "type": "tuple[]"
      }
    ],
    "name": "aggregate",
    "outputs": [
      {"internalType": "uint256", "name": "blockNumber", "type": "uint256"},
      {"internalType": "bytes[]", "name": "returnData", "type": "bytes[]"}
    ],
    "stateMutability": "payable",
    "type": "function"
  }
];

interface TokenBalance {
  symbol: string;
  address: string;
  balance: string;
  hasBalance: boolean;
}

interface TokenDetectionResult {
  hasTokens: boolean;
  tokens: TokenBalance[];
  error?: string;
}

/**
 * Check if a user has any BEP20 tokens using Multicall3
 */
export async function detectUserTokens(userAddress: string, provider?: ethers.Provider): Promise<TokenDetectionResult> {
  if (!provider) {
    try {
      provider = getEthersProvider();
    } catch (error) {
      console.error("Failed to get provider:", error);
      return {
        hasTokens: false,
        tokens: [],
        error: "Failed to get blockchain provider"
      };
    }
  }
  try {
    // Create Multicall3 contract instance
    const multicall = new ethers.Contract(MULTICALL3_ADDRESS, MULTICALL3_ABI, provider);

    // Prepare calls for each token
    const calls = Object.entries(BSC_TOKENS).map(([symbol, tokenAddress]) => ({
      target: tokenAddress,
      callData: BALANCE_OF_SIGNATURE + userAddress.slice(2).padStart(64, '0')
    }));

    // Execute multicall
    const [, returnData] = await multicall.aggregate(calls);

    // Parse results
    const tokens: TokenBalance[] = [];
    let hasTokens = false;

    Object.entries(BSC_TOKENS).forEach(([symbol, tokenAddress], index) => {
      try {
        // Decode balance from returned data
        const balanceHex = returnData[index];
        const balance = ethers.getBigInt(balanceHex);
        const balanceString = balance.toString();
        const hasBalance = balance > BigInt(0);

        if (hasBalance) {
          hasTokens = true;
        }

        tokens.push({
          symbol,
          address: tokenAddress,
          balance: balanceString,
          hasBalance
        });
      } catch (error) {
        console.warn(`Failed to parse balance for ${symbol}:`, error);
        tokens.push({
          symbol,
          address: tokenAddress,
          balance: "0",
          hasBalance: false
        });
      }
    });

    return {
      hasTokens,
      tokens
    };

  } catch (error) {
    console.error("Failed to detect user tokens:", error);
    return {
      hasTokens: false,
      tokens: [],
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
}

/**
 * Cache for token detection results (5 minute TTL)
 */
const tokenDetectionCache = new Map<string, { result: TokenDetectionResult; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Cached version of token detection
 */
export async function detectUserTokensCached(userAddress: string, provider?: ethers.Provider): Promise<TokenDetectionResult> {
  const cacheKey = userAddress.toLowerCase();
  const cached = tokenDetectionCache.get(cacheKey);

  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.result;
  }

  const result = await detectUserTokens(userAddress, provider);
  
  tokenDetectionCache.set(cacheKey, {
    result,
    timestamp: Date.now()
  });

  return result;
}

/**
 * Clear cache for a specific user (useful after transactions)
 */
export function clearTokenDetectionCache(userAddress?: string) {
  if (userAddress) {
    tokenDetectionCache.delete(userAddress.toLowerCase());
  } else {
    tokenDetectionCache.clear();
  }
}